/**
 * CameraPoseGate — change-detector for a camera pose.
 *
 * `changed(camera)` returns true (and re-snapshots) when the camera has
 * moved or rotated beyond a tiny epsilon since the last true. Used to gate
 * per-frame 3D→2D projections and billboarding that only need recomputing
 * while the camera is actually moving (screen-anchored DOM overlays, sidebet
 * button billboards). While the camera is parked — the whole betting phase —
 * the consumer skips its projection math entirely.
 *
 * `force()` makes the next changed() return true regardless: call it on
 * show, anchor change, and resize so the first frame after any discrete
 * change always recomputes.
 */

import * as THREE from 'three';

export class CameraPoseGate {
  private lastPos = new THREE.Vector3();
  private lastQuat = new THREE.Quaternion();
  private dirty = true;

  force(): void {
    this.dirty = true;
  }

  changed(camera: THREE.Camera): boolean {
    const moved =
      this.dirty
      || this.lastPos.distanceToSquared(camera.position) > 1e-8
      || Math.abs(this.lastQuat.dot(camera.quaternion)) < 1 - 1e-7;
    if (moved) {
      this.dirty = false;
      this.lastPos.copy(camera.position);
      this.lastQuat.copy(camera.quaternion);
    }
    return moved;
  }
}
